import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// API functions
import {
  listProjects,
  createProject,
  hardDeleteProject,
  updateProjectTags,
  cloneProject,
} from "views/APIs/NewProjectArtifacts";

import { inviteUserAPI } from "views/APIs/APIUtility";

// @material-ui/icons
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";

// core components
import NotificationContext from "views/Components/Context.js";
import { CloudUpload, Create, DoneAll } from "@material-ui/icons";
import CreateCourseCard from "views/Components/CreateCourseCard";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import CreateArtifactProjectModal from "views/Components/CreateArtifactProjectModal";
import ArtifactProjectDetails from "../Models/ArtifactProjectDetails";

const useStyles = makeStyles({
  ...styles,
});

export default function ArtifactProjectsDesign() {
  const asset_identifier = "session";
  const images = require.context("assets/img/create_cards_16_9/", true);

  // state variables
  const { path, url } = useRouteMatch();
  const classes = useStyles();
  const [createProjectModal, setCreateProjectModal] = React.useState(false);
  const [newProjectDetails, setNewProjectDetails] = React.useState(
    new ArtifactProjectDetails(),
  );
  const [allProjects, setAllProjects] = React.useState([]);
  const [initialProjects, setInitialProjects] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });

  const [projectAdded, setProjectAdded] = React.useState(false);
  const { showNotification } = React.useContext(NotificationContext);

  const handleDeleteProject = async (projectData) => {
    try {
      await hardDeleteProject(projectData._id);
      setAllProjects(
        allProjects.filter((project) => project._id !== projectData._id),
      );
      showNotification("Project deleted successfully", "success");
    } catch (error) {
      showNotification(error.message, "danger");
    }
  };

  const handleCloneProject = async (projectData) => {
    try {
      setLoading({ loading: true, message: "Cloning Project..." });
      const clonedProject = await cloneProject(projectData._id);
      setAllProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(clonedProject),
      ]);
      setProjectAdded(true);
      showNotification("Project cloned successfully", "success");
    } catch (error) {
      showNotification(error.message, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const requestData = {
        collection_type: "sessions",
        project_id: data._id,
        invitee_email: data.email,
      };

      // You need to implement inviteUserAPI for sessions in your backend
      const response = await inviteUserAPI(requestData);
      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allProjects.find(
        (asset) => asset._id === updatedProjectId,
      );
      if (!updatedAsset) {
        console.warn(
          "⚠️ Could not find the matching asset for ID:",
          updatedProjectId,
        );
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };
      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" }
          : invitee,
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email,
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allProjects state
      const updatedProjects = allProjects.map((project) =>
        project._id === updatedProjectId ? newAsset : project,
      );

      setAllProjects(updatedProjects);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      console.error("Error sending invite:", error);
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading({ loading: true, message: "Loading Sessions..." });
        const data = await listProjects();
        console.log("Fetched projects data:", data);
        const mappedData = data.map(_mapResourceToAppType);
        console.log("Mapped projects data:", mappedData);
        setAllProjects(mappedData);
        setInitialProjects(mappedData);
        
      } catch (error) {
        showNotification(error.message, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchData();
  }, []);

  const handleCreateProject = async () => {
    setLoading({ loading: true, message: "Creating Project..." });
    try {
      const data = await createProject({
        name: newProjectDetails.name,
        description: newProjectDetails.description,
      });

      // Navigate to the project page
      window.open(
        `/create/sessions/session/${data.project_id}`,
        "_blank",
      );
      setAllProjects([...allProjects, _mapResourceToAppType(data)]);
      setProjectAdded(true);
      showNotification("Session created successfully", "success");
    } catch (error) {
      showNotification(error.message, "danger");
    } finally {
      handleCreateProjectModalClose();
      setLoading({ loading: false, message: "" });
    }
  };

  const clearCreateModal = () => {
    setNewProjectDetails(new ArtifactProjectDetails());
  };

  const handleCreateProjectModalClose = () => {
    setCreateProjectModal(false);
    clearCreateModal();
  };

  const getStatusIcon = (status) => {
    // For artifact projects, we'll use a simple status system
    switch (status) {
      case "Active":
        return <Create style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      case "Ready":
        return <CloudUpload style={{ color: "#435966" }} />;
      default:
        return <Create style={{ color: "#435966" }} />;
    }
  };

  const _mapResourceToAppType = (resource) => {
    return {
      _id: resource.project_id || resource._id,
      name: resource.name,
      description: resource.description,
      users: resource.users || [],
      status: "Active", // Default status
      tags: resource.tags || [], // Default tags
      invitees: [], // Default invitees
    };
  };

  const handleSaveLabel = async (projectData) => {
    try {
      await updateProjectTags(projectData._id, projectData.tags);
      setAllProjects((prevProjects) =>
        prevProjects.map((project) =>
          project._id === projectData._id
            ? { ...project, tags: projectData.tags }
            : project,
        ),
      );
      showNotification("Tags updated successfully", "success");
    } catch (error) {
      showNotification(error.message, "danger");
    }
  };

  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h5"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateArtifactProjectModal
        newProjectDetails={newProjectDetails}
        setNewProjectDetails={setNewProjectDetails}
        createProjectModal={createProjectModal}
        handleCreateProjectModalClose={handleCreateProjectModalClose}
        handleCreateProject={handleCreateProject}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Sessions Dashboard"
        welcomeSubtitle="Start by creating a new session or reviewing your existing sessions."
        allAssets={allProjects}
        setAllAssets={setAllProjects}
        initialAssets={initialProjects}
        noAssetMessage="Create or Review your sessions to get started."
        linkPath="/session/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateProjectModal}
        createAssetButtonText="Create New Session"
        handleSaveLabel={handleSaveLabel}
        handleInviteSend={handleInviteSend}
        asset_identifier={asset_identifier}
        handleDeleteAsset={handleDeleteProject}
        handleCloneAsset={handleCloneProject}
        assetAdded={projectAdded}
        setAssetAdded={setProjectAdded}
        setInitialAssets={setInitialProjects}
        workflowConfig={{
          type: "session",
          title: "Session Workflow",
          description:
            "The session workflow helps you manage and organize your session artifacts through a structured pipeline process.",
          steps: [
            {
              label: "1. Upload input files & define instructions",
              body:
                "Start by uploading your input files and defining clear instructions for processing",
            },
            {
              label: "2. Run pipelines & generate artifacts",
              body:
                "Execute various pipelines to transform your inputs into structured artifacts",
            },
            {
              label: "3. Review, edit & manage artifacts",
              body:
                "Review generated artifacts, make edits, and manage the entire collection through the session lifecycle",
            },
          ],
          videoUrl: "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/session.mp4", // Add demo video URL when available
          examples: [
            {
              title: "Document Processing Pipeline",
              description:
                "Transform raw documents into structured artifacts using AI-powered processing pipelines.",
              link: "#",
              mediaUrl: "",
              mediaAlt: "Document Processing Demo",
            },
            {
              title: "Data Analysis Project",
              description:
                "Organize data analysis workflows with input datasets, processing scripts, and output reports.",
              link: "#",
              mediaUrl: "",
              mediaAlt: "Data Analysis Demo",
            },
          ],
        }}
      />
    </div>
  );
}
